import { formatValueWithUnits } from "../../measurement/UnitFormatter";
import { DtConstants } from "../schema/DtConstants";
import { AttributeType, findBucketIdxByValue } from "../schema/Attribute";
import { SystemClass, SystemClassNames } from "../schema/system-class";
import { System } from "../connectivity/Connectivity";
import { ElementFlags, ElementIdWithFlagsEncodedLength } from "../schema/dt-schema";
import i18n from "i18next";
import { TandemCategories } from "../schema/TandemCategories";
import { SelectionMode } from "../../wgs/scene/SelectionMode";
import { sortedArrayIntersection } from "./sortedArray";
import { AssetCheck } from "../schema/AssetCheck";
import { matchClassification } from "../schema/matchClassification";
/**
 * @typedef {object} FacetSettings configuration object used to deserialize a Facet
 * @property {string} id required id that represents a facet type.
 *          One of: "levels" | "systems" | "attributes" | "models" | "cats" | "fams" | "types" | "spaces"
 * @property {string|undefined} attributeHash attribute hash.
 *          Required for id: "attributes"
 * @property {boolean|undefined} hidden flag to indicate a hidden facet
 * @property {boolean|undefined} isIntersectionFilter flag to indicate an intersection facet filter
 */

const ROOT_ID = Symbol('root');

export const UNASSIGNED = "(Unassigned)";
const unassignedLabel = () => i18n.t("(Unassigned)");
export const UNCLASSIFIED = "(Unclassified)";
const unclassifiedLabel = () => i18n.t("(Unclassified)");
export const UNDEFINED = "(Undefined)";
const undefinedLabel = () => i18n.t("(Undefined)");

export const FacetTypes = {
  levels: "levels",
  tandemCategories: "tandemCategories",
  assemblyCode: "systems",
  classifications: "classifications",
  attributes: "attributes",
  models: "models",
  categories: "cats",
  families: "fams",
  parameters: "parameters",
  priority: "priority",
  ticketStatus: "ticketStatus",
  openDate: "openDate",
  closeDate: "closeDate",
  types: "types",
  spaces: "spaces",
  status: "status",
  systemClasses: "systemClasses",
  mepSystems: "mepSystems",
  assets: "assets"
};

export const AllFacetsScopes = -1;
export const FacetsScope = Object.freeze({
  Context: 0,
  Tickets: 1
});

export const DISPLAY_COLOR_NO_FILL = '';

export const PALETTE_SOURCE_FILES = [
'#00ffff', // 'aqua'
'#8a2be2', // 'blueviolet'
'#ff7f50', // 'coral'
'#0000cd', // 'mediumblue'
'#ff1493', // 'deeppink'
'#1e90ff', // 'dodgerblue'
'#ffd700', // 'gold'
'#adff2f', // 'greenyellow'
'#006400', // 'darkgreen'
'#ff6347', // 'tomato'
'#ffff00', // 'yellow'
'#6495ed', // 'cornflowerblue'
'#87ceeb', // 'skyblue'
'#808000', // 'olive'
'#ff00ff', // 'magenta'
'#90ee90' // 'lightgreen'
];

export const PALETTE_ATTRIBUTE_STRING = [
'#0792D7',
'#5278c8',
'#3ABBBA',
'#9C6BCE',
'#D467D5',
'#3CC580',
'#689ED4',
'#90D847',
'#CE5C95',
'#3BD23A',
'#E0AF4B'];


export const PALETTE_ATTRIBUTE_STRINGLIST = [
'#0792D7',
'#FFC219',
'#3CC57D',
'#5F60FF',
'#C34922',
'#3ABBBA',
'#90D847',
'#5078C8',
'#D467D5',
'#E0AF4B',
'#9C6BCE',
'#689ED4',
'#3BD23A'];


const PALETTE_SYSTEMS = {
  [UNCLASSIFIED]: '#191970', // 'midnightblue', //Unclassified: 0
  'A': '#708090', // 'slategrey', //Substructure:  1,	//Uniformat A
  'B': '#ffe4e1', // 'mistyrose', //Shell:         2,	//Uniformat B
  'C': '#add8e6', // 'lightblue', //Interior:      3,	//Uniformat C
  'D10': '#4169e1', // 'royalblue', //Conveying:     4,	//Uniformat D10
  'D20': '#00ff00', // 'lime', //Plumbing:      5,	//Uniformat D20
  'D30': '#ffff00', // 'yellow', //HVAC:          6,	//Uniformat D30
  'D40': '#ff0000', // 'red', //Fire:          7,	//Uniformat D40
  'D50': '#ff00ff', // 'magenta', //Electrical:    8,	//Uniformat D50
  'E': '#ffa500', // 'orange', //Equipment:     9,	//Uniformat E
  'F': '#ff7f50', // 'coral', //Special:      10,	//Uniformat F
  'G': '#006400' // 'darkgreen', //Sitework:     11,	//Uniformat G
};


const PALETTE_TANDEM_CATS = {
  [UNCLASSIFIED]: '#191970', // 'midnightblue', //Unclassified: 0
  'B': '#c0c0c0', // 'silver', //Building Element
  'M': '#4169e1', // 'royalblue', //Mechanical(HVAC)
  'E': '#00ffff', // 'cyan', //Electrical
  'P': '#00ff00', // 'lime', //Plumbing
  'P.Sp': '#ff0000', // 'red', //Sprinklers
  'Q': '#f4a460', // 'saqndybrown', //Equipment
  'Q.FA': '#ff0000', // 'red', //Fire Alarm device
  'L': '#006400' // 'darkgreen', //Location
};

const SC = SystemClass;
const PALETTE_SYSTEMCLASS = {
  [UNCLASSIFIED]: '#9f9f9f', //Unclassified: 0
  [1 << SC.SupplyAir]: '#006BFF', // A supply air duct system
  [1 << SC.ReturnAir]: '#C5B235', // A return air duct system
  [1 << SC.ExhaustAir]: '#EC775E', // Obsolete classification not used anymore
  [1 << SC.SupplyHydronic]: '#00ffff', // A hydronic supply piping system
  [1 << SC.ReturnHydronic]: '#ff00ff', // A hydronic return piping system
  [1 << SC.DomesticHotWater]: '#ff6718', // A hot water piping system
  [1 << SC.DomesticColdWater]: '#3777ff', // A cold water piping system
  [1 << SC.Sanitary]: '#03c503', // A sanitary piping system
  [1 << SC.PowerCircuit]: '#ffff00', // An electrical circuit
  [1 << SC.Vent]: '#ffc000', // A piping ventilation system
  [1 << SC.Controls]: '#7f7f7f', // An electrical circuit classified as controls

  [1 << SC.FireProtectWet]: '#ff0000', // A wet fire protection system
  [1 << SC.FireProtectDry]: '#ff0000', // A dry fire protection system
  [1 << SC.FireProtectPreaction]: '#ff0000', // A preaction fire protection system

  //[1<<SC.OtherAir]: '#7f7f7f', // An user classified air duct system
  //[1<<SC.OtherPipe]: '#7f7f7f', // An user classified piping system
  [1 << SC.FireProtectOther]: '#ff0000', // A user defined fire protection system

  //[1<<SC.Communication]: '#7f7f7f', // An electrical circuit classified as communication
  //[1<<SC.DataCircuit]: '#7f7f7f', // An electrical data circuit
  //[1<<SC.Telephone]: '#7f7f7f', // An electrical circuit classified as telephone
  //[1<<SC.Security]: '#7f7f7f', // An electrical security circuit
  //[1<<SC.FireAlarm]: '#7f7f7f', // An electrical fire alarm circuit
  //[1<<SC.NurseCall]: '#7f7f7f', // An electrical circuit classified as nurse call
  //[1<<SC.SwitchTopology]: '#7f7f7f', // An electrical switch system
  //[1<<SC.CableTrayConduit]: '#7f7f7f', // Indicates the default type for cable trays and conduits

  [1 << SC.Storm]: '#7f00ff' // Reserved for future use

  //Not seen in any sample files -- add strings to the mapping below if/when encountered
  //[1<<SC.CondensateDrain]: '#7f7f7f', // Reserved for future use
  //[1<<SC.Recirculation]: '#7f7f7f', // Reserved for future use

  //[1<<SC.Fitting]: '#7f7f7f', // Reserved for application usage, used for UI filtering in Family Editor
  //[1<<SC.Global]: '#7f7f7f', // Reserved for application usage, used for UI filtering in Family Editor
  //[1<<SC.PowerBalanced]: '#7f7f7f', // Equivalent with PowerCircuit
  //[1<<SC.PowerUnBalanced]: '#7f7f7f', // Equivalent with PowerCircuit
};

export const PALETTE_TICKET_PRIORITY = {
  [DtConstants.TicketPriorities.Trivial]: '#7D4CDB',
  [DtConstants.TicketPriorities.Low]: '#00C781',
  [DtConstants.TicketPriorities.Medium]: '#FAA21B',
  [DtConstants.TicketPriorities.High]: '#DD2222',
  [DtConstants.TicketPriorities.Critical]: '#FF0000'
};

// trailingLabels is an array of string labels that are supposed to be sorted
// to the back of the resulting list, in the order they appear in the array
function sortByLabel(trailingLabels) {
  return (a, b) => {
    const va = a.label?.toString() || a.id?.toString() || '';
    const vb = b.label?.toString() || b.id?.toString() || '';

    const ca = trailingLabels.indexOf(va);
    const cb = trailingLabels.indexOf(vb);

    if (ca == -1 && cb == -1) {
      return va.localeCompare(vb);
    } else if (ca == -1) {
      return -1;
    } else if (cb == -1) {
      return 1;
    } else {
      return ca - cb;
    }
  };
}

function makeNullBatch(id, label, idsByModel, filter) {
  const selected = filter.has(id);
  const implicitlySelected = filter.size === 0;
  return {
    facets: [new FacetNode({
      id,
      label: label,
      isUnassignedId: true,
      count: getTotalIdCount(idsByModel),
      selected,
      implicitlySelected,
      ids: idsByModel
    })],
    idsByModelFiltered: selected || implicitlySelected ? idsByModel : new Map()
  };
}

export class FacetDef {
  /**
   */
  constructor(hidden) {
    this.id = null;
    this.filter = new Set();
    this.palette = [];
    this.hidden = hidden ?? false;
    this.isIntersectionFilter = false;
  }

  /**
   * @param {FacetSettings} settings
   * @param {import("../DtFacility").DtFacility} facility
   * @param {Object} options FacetsManager options
   */
  async init(settings, facility, options) {
    this.setSettings(settings);
    this.options = options;
  }

  /**
   * @returns {FacetSettings}
   */
  getSettings() {
    return {
      id: this.id,
      hidden: this.hidden,
      isIntersectionFilter: this.isIntersectionFilter,
      ...this.settings
    };
  }

  /**
   * @param {FacetSettings} settings
   */
  setSettings({ hidden, isIntersectionFilter } = {}) {
    this.settings = {};
    this.hidden = hidden ?? this.hidden;
    this.isIntersectionFilter = isIntersectionFilter ?? this.isIntersectionFilter;
  }

  isLoaded() {
    return true;
  }

  /**
   * @param {FacetNode[]} mergedFacetNodes
   * @returns {FacetNode[]}
   */
  formatMergedFacetNodes(mergedFacetNodes) {
    return mergedFacetNodes.sort(this.getSorter());
  }

  getSorter() {
    return sortByLabel([UNASSIGNED]);
  }

  /**
   * @typedef {Object} FacetClassificationResult
   * @property {FacetNode[]} facets
   * @property {Map<import("../DtModel").DtModel, Iterable<Number>>} idsByModelFiltered
   */

  /**
   * @param {Map<import("../DtModel").DtModel, Iterable<Number>>} idsByModel
   * @returns {FacetClassificationResult}
   */
  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {
    console.log("Not yet implemented for", this.id);
    return makeNullBatch(UNDEFINED, undefinedLabel(), idsByModel, ignoreFilter ? new Set() : this.filter);
  }

  async cacheExternalDataForModels(models) {
    return Promise.all(models.map((model) => this._cacheExternalDataForModel(model)));
  }

  async _cacheExternalDataForModel(model) {}
  clearCachedExternalDataForModel(model) {}

  addFilter(f) {
    this.filter.add(f);
  }

  resetFilters() {
    this.filter.clear();
  }

  supportsWildcardFilters() {
    return false;
  }
}

// deduplicates levels with the same name, removes levels without name
// and injects the "next" higher level (elevation wise), if any
function prepareLevels(lvlId2lvl) {

  const levelsDeduplicated = new Map();
  for (let level of Object.values(lvlId2lvl)) {
    if (level.name) {
      if (!levelsDeduplicated.has(level.name)) {
        levelsDeduplicated.set(level.name, { ...level });
      } else {
        const existing = levelsDeduplicated.get(level.name);
        if (FacetLevels.sortByElev(level, existing) < 0) {
          levelsDeduplicated.set(level.name, { ...level });
        }
      }
    }
  }

  const sorted = Array.from(levelsDeduplicated.values()).
  filter((level) => Number.isFinite(level.elev)).
  sort(FacetLevels.sortByElev);
  for (let i = 0; i < sorted.length - 1; ++i) {
    sorted[i].next = sorted[i + 1];
  }

  const result = {};
  for (let id in lvlId2lvl) {
    const level = lvlId2lvl[id];
    // NOTE: this results in treating levels without name as unassigned
    result[id] = level.name ? levelsDeduplicated.get(level.name) : null;
  };

  return result;
}

export class FacetLevels extends FacetDef {

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.levels;
    this.label = i18n.t("Levels");
  }

  static sortByElev(a, b) {

    if (a.elev === undefined) {
      if (b.elev === undefined) {
        return a.order - b.order;
      } else {
        return 1;
      }
    } else if (b.elev === undefined) {
      return -1;
    }

    return a.elev - b.elev;
  }

  getSorter() {
    return FacetLevels.sortByElev;
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter, allowEmptyFacets }) {

    const noLevel = {
      id: UNASSIGNED,
      label: unassignedLabel(),
      elev: undefined,
      order: Infinity,
      isUnassignedId: true
    };

    const makeFacetItem = (level) => {

      if (!level || !level.name) return noLevel;

      return {
        id: level.name,
        label: level.name,
        elev: level.elev,
        height: level.height,
        order: level.order ?? Infinity,
        isUnassignedId: false
      };
    };

    const useTopLevel = this.options.useTopLevel;
    const filter = ignoreFilter ? new Set() : this.filter;
    const facetTracker = new FacetTracker(makeFacetItem, filter, this.isIntersectionFilter);

    for (let [model, ids] of idsByModel) {

      facetTracker.nextModel(model, ids);


      const lvlId2lvl = prepareLevels(model.getLevels());
      const dbId2levelId = model.getData().dbId2levelId;
      const dbId2topLevel = model.getData().dbId2topLevel;
      const it = model.getInstanceTree();


      for (let dbId of ids) {

        facetTracker.nextElement(dbId);

        let levelId = dbId2levelId[dbId];
        if (!levelId && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          levelId = dbId2levelId[dbId];
        }

        let level = lvlId2lvl[levelId];

        //Assign level objects to themselves, in case we are displaying them
        if (!level) {
          level = lvlId2lvl[dbId];
        }

        if (!level) {
          facetTracker.addFacet(null);
          continue;
        }

        if (useTopLevel) {
          // bit weird logic here: top level is supposed to be excluded, 
          // but only if it's different from the base one
          let topLevel = lvlId2lvl[dbId2topLevel[dbId]];
          if (!topLevel || level === topLevel) {
            facetTracker.addFacet(level, level.name);
          } else {
            while (level && level !== topLevel) {
              facetTracker.addFacet(level, level.name);
              level = level.next;
            }
          }
        } else
        {
          facetTracker.addFacet(level, level.name);
        }
      }
    }

    if (allowEmptyFacets) {
      // Creates ID-only empty facets for filters with no elements
      const facet = {};
      for (const id of filter) {
        if (id === UNASSIGNED) {
          facetTracker.addIfMissing(null);
        } else {
          facet.name = id;
          facetTracker.addIfMissing(facet, id);
        }
      }
    }

    return facetTracker.done(idsByModel, allowEmptyFacets);
  }
}

export class FacetSystems extends FacetDef {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.mepSystems;
    this.label = i18n.t("Systems");
    this.palette = Object.values(PALETTE_SYSTEMCLASS);
    this.paletteById = PALETTE_SYSTEMCLASS;

    this._systems = null;
    this._attributesData = new Map();
  }

  setSettings(settings) {
    super.setSettings(settings);
    this.settings.attributeHashes = settings.attributeHashes;
  }

  getCachedAttributesData(model) {
    return this._attributesData.get(model);
  }

  async cacheExternalDataForModels(models) {
    if (!this._systems) {
      const defaultModel = models.find((model) => model.isDefault());
      // systems are defined in the default model. we fetch them only if given the default
      // model to avoid a deadlock in the loading queue
      if (defaultModel) {
        this._systems = await defaultModel.facility.systemsManager.getAll();

        const attrHashes = new Set();
        for (const s of this._systems) {
          if (s.subsystemAttrHash) {
            attrHashes.add(s.subsystemAttrHash);
          }
        }
        this.settings.attributeHashes = Array.from(attrHashes);
      }
    }
    return super.cacheExternalDataForModels(models);
  }

  async _cacheExternalDataForModel(model) {

    if (!this._attributesData.has(model) && this.settings.attributeHashes?.length > 0) {
      const facets = await model.getCustomFacetsDebounced(this.settings.attributeHashes);

      if (!facets) {
        // Attribute data doesn't exist in the model
        this._attributesData.set(model, null);
      } else {
        const byHash = {};
        for (let i = 0; i < this.settings.attributeHashes.length; i++) {
          if (!facets[i]) continue;
          const hash = this.settings.attributeHashes[i];
          byHash[hash] = facets[i];
        }
        this._attributesData.set(model, byHash);
      }
    }
  }

  clearCachedExternalDataForModel(model) {
    if (model.isDefault()) {
      this._systems = null;
      this.settings.attributeHashes = null;
    }

    this._attributesData.delete(model);
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const filter = ignoreFilter ? new Set() : this.filter;
    const systems = this._systems;
    if (!systems || systems.length == 0) {
      return makeNullBatch(UNASSIGNED, unassignedLabel(), idsByModel, filter);
    }

    const makeFacetItem = (value) => {
      if (!value) {
        return {
          id: UNASSIGNED,
          glowEffectId: -1,
          label: unassignedLabel(),
          isUnassignedId: true
        };
      }

      return value instanceof System ? {
        id: value.id,
        glowEffectId: value.filter.systemClasses,
        label: value.name
      } : {
        id: value.system.id + ":" + value.id, // subsystem ids are not unique, so we need to prefix them with the system id
        label: value.name,
        system: value.system
      };
    };

    const facetTracker = new FacetTracker(makeFacetItem, filter, this.isIntersectionFilter);

    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);
      const dbId2SysClass = model.getData().dbId2SystemClass;
      const it = model.getInstanceTree();

      for (let dbId of ids) {
        facetTracker.nextElement(dbId);
        let sysClass = dbId2SysClass[dbId];
        if (!sysClass && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          sysClass = dbId2SysClass[dbId];
        }
        if (!sysClass) {
          facetTracker.addFacet(null);
        } else {
          let matched = false;
          for (const system of systems) {
            const isSystemElement = system.filter.systemClasses & sysClass;
            if (!isSystemElement) {
              // Element does not belong to this system
              continue;
            }

            const modelAttrData = this.getCachedAttributesData(model);
            const subsystem = system.getElementSubsystem(modelAttrData, dbId);
            if (subsystem) {
              // Element belongs to subsystem
              facetTracker.addFacet(subsystem);
              matched = true;
              continue;
            }

            // Element belongs to the system, but not to any subsystem
            facetTracker.addFacet(system);
            matched = true;
          }
          if (!matched) {
            facetTracker.addFacet(null);
          }
        }
      }
    }
    return facetTracker.done(idsByModel);
  }

  formatMergedFacetNodes(mergedFacetNodes) {
    const listWithHierarchy = [];
    const systemNodes = {};

    // first pass to process top-level system nodes
    for (const node of mergedFacetNodes) {
      const { id, system } = node;
      if (system) continue;

      if (!systemNodes[id]) {
        node.children ??= [];
        node.selected = this.filter.has(id);

        systemNodes[id] = node;
        listWithHierarchy.push(node);
      }
    }

    // second pass to process subsystem nodes and assign them to their parent system nodes
    // and add missing parent system nodes
    for (const node of mergedFacetNodes) {
      const { system } = node;
      if (!system) continue;
      if (!node.children) node.children = [];

      let systemNode = systemNodes[system.id];
      if (!systemNode) {
        // Add missing parent system node because all elements in the system belong to subsystems
        systemNode = systemNodes[system.id] = new FacetNode({
          id: system.id,
          children: [],
          count: 0,
          label: system.name,
          glowEffectId: system.filter.systemClasses,
          selected: this.filter.has(system.id),
          implicitlySelected: this.filter.size === 0
        });
        listWithHierarchy.push(systemNode);
      }

      systemNode.children.push(node);
    }

    return listWithHierarchy.sort(this.getSorter());
  }

  getSorter() {
    return sortByLabel([UNASSIGNED]);
  }
}

export class FacetSpaces extends FacetDef {
  // Hide floors, ceilings and roofs so that one can see inside the room
  static isHiddenFacetCategory(catId) {
    return catId === TandemCategories.Floors || catId === TandemCategories.Roofs || catId === TandemCategories.Ceilings;
  }

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.spaces;
    this.label = i18n.t("Spaces");
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter, modelsByUrn, allowEmptyFacets }) {

    const UnassignedT = unassignedLabel();

    function makeFacetItem(roomInfo) {
      return roomInfo ? {
        id: FacetSpaces.makeId(roomInfo),
        externalId: roomInfo.externalId,
        label: roomInfo.name || UnassignedT,
        dbId: roomInfo.dbId,
        roomSrcModel: roomInfo.model,
        isMEPSpace: roomInfo.isMEPSpace
      } : {
        id: UNASSIGNED,
        label: UnassignedT,
        dbId: 0,
        roomSrcModel: null,
        isMEPSpace: false,
        isUnassignedId: true
      };
    }

    const filter = ignoreFilter ? new Set() : this.filter;
    const facetTracker = new FacetTracker(makeFacetItem, filter, this.isIntersectionFilter);

    for (let [model, ids] of idsByModel) {

      facetTracker.nextModel(model, ids);

      let dbIdToTanCatId = model.getData().dbId2TandemCategory;
      let dbIdToFlags = model.getData().dbId2flags;
      let roomMap = model.getRooms();
      const it = model.getInstanceTree();

      for (let dbId of ids) {
        facetTracker.nextElement(dbId);

        let catId = dbIdToTanCatId[dbId];

        if (FacetSpaces.isHiddenFacetCategory(catId)) {
          facetTracker.addFacet(null);
          continue;
        }

        //Assign rooms/spaces to themselves in case we are displaying them
        if (dbIdToFlags[dbId] === ElementFlags.Room) {
          let myRoom = roomMap[dbId];
          if (myRoom) {
            facetTracker.addFacet(myRoom);
          } else {
            facetTracker.addFacet(null);
          }
          continue;
        }

        let rooms = model.getRoomsOfElement(dbId, modelsByUrn);
        if (!rooms.length && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          rooms = model.getRoomsOfElement(dbId, modelsByUrn);
        }
        if (rooms.length > 0) {
          for (let room of rooms) {
            facetTracker.addFacet(room);
          }
        } else {
          facetTracker.addFacet(null);
        }

      }
    }

    if (allowEmptyFacets) {
      for (const id of filter) {
        if (id === UNASSIGNED) {
          facetTracker.addIfMissing(null);
          continue;
        }

        const { modelId, elementId } = FacetSpaces.decodeId(id);
        const room = modelsByUrn[modelId]?.getRooms()[elementId];
        if (room) {
          facetTracker.addIfMissing(room);
        }
      }
    }

    //console.profileEnd('classify space');
    return facetTracker.done(idsByModel, allowEmptyFacets);
  }

  static makeId(roomInfo) {
    return `${roomInfo.model.urn()}:${roomInfo.externalId}${roomInfo.isMEPSpace ? ':MEPSpace' : ''}`;
  }

  static decodeId(id) {
    // decoding it right to left to leave URN prefix flexible.
    const endIdx = id.length - (id.endsWith('MEPSpace') ? ':MEPSpace'.length : 0);
    const sepIdx = endIdx - ElementIdWithFlagsEncodedLength - 1;
    return {
      modelId: id.substring(0, sepIdx),
      elementId: id.substring(sepIdx + 1)
      // not returning isMEPSpace as it is only needed for uniqueness.
    };
  }
}

export class FacetSystemClasses extends FacetDef {

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.systemClasses;
    this.label = i18n.t("System Classes");
    this.palette = Object.values(PALETTE_SYSTEMCLASS);
    this.paletteById = PALETTE_SYSTEMCLASS;
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const makeFacetItem = (systemIdx) => {
      return systemIdx >= 0 ? {
        id: systemIdx,
        label: SystemClassNames[systemIdx]
      } : {
        id: -1,
        label: undefinedLabel(),
        isUnassignedId: true
      };
    };
    const facetTracker = new FacetTracker(makeFacetItem, ignoreFilter ? new Set() : this.filter, this.isIntersectionFilter);

    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);

      const masks = model.getData().dbId2SystemClass;
      const it = model.getInstanceTree();

      for (let dbId of ids) {
        facetTracker.nextElement(dbId);
        let mask = masks[dbId];
        if (!mask && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          mask = masks[dbId];
        }
        if (mask) {
          for (let i = 0; i < SystemClassNames.length; i++) {
            if (mask & 1 << i) {
              facetTracker.addFacet(i);
            }
          }
        } else {
          facetTracker.addFacet(-1);
        }
      }
    }
    return facetTracker.done(idsByModel);
  }

  getSorter() {
    return ({ id: a }, { id: b }) => {
      //Make the undefined system class (-1) sort last
      if (a < 0) a = 1e10;
      if (b < 0) b = 1e10;

      return a - b;
    };
  }
}

export class FacetParameters extends FacetDef {

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.parameters;
    this.label = i18n.t("Parameters");
    this._classifiedAssets = new Map();
  }

  get hideClustering() {
    return true;
  }

  get hideColoring() {
    return true;
  }

  // returns an object mapping dbId to the set of keys of properties that have stream reading
  // e.g., {2: {'z:BA', 'z:EQ', 'z:Ew'}, 8: {'z:BA', 'z:EQ', 'z:Ew'}}
  async _getDbIdToPropsWithStreamReading(facility) {
    const streamManager = facility.getStreamManager();
    const streamIds = await streamManager.getStreamIds();
    const lastReadings = await streamManager.getLastReadings(streamIds);

    const dbIdToPropsWithStreamReading = {};
    streamIds.forEach((streamId, i) => {
      const streamAttrKeys = Object.keys(lastReadings[i] || {}); // e.g., [z:BA, z:EQ, z:Ew] (corresponding to CO2, Humidity, temp)
      dbIdToPropsWithStreamReading[streamId] = new Set(streamAttrKeys);
    });

    return dbIdToPropsWithStreamReading;
  }

  async _getClassifiedAssets(model) {
    const propFam = DtConstants.ColumnFamilies.DtProperties;
    const propTypeFam = "type|" + propFam;

    const { rows, cols } = await model.getClassifiedAssets();

    const rowByDbId = [];
    for (let row of rows) {
      rowByDbId[row[DtConstants.QC.LmvDbId]] = row;
    }

    const propertyById = new Map();
    for (const col of cols) {
      const id = col.id;
      if (id.startsWith(propFam) || id.startsWith(propTypeFam)) {
        propertyById.set(id, col);
      }
    }

    return { rowByDbId, propertyById };
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const makeFacetItem = (propDetails) => {
      return propDetails ? {
        id: propDetails.id,
        key: propDetails.property.id,
        label: propDetails.property.name,
        isTagged: propDetails.isTagged,
        category: propDetails.property.category,
        flags: propDetails.property.flags,
        isType: propDetails.property.id.startsWith("type|")
      } : {
        id: UNASSIGNED,
        label: unassignedLabel(),
        isUnassignedId: true
      };
    };

    const facetTracker = new FacetTracker(makeFacetItem, ignoreFilter ? new Set() : this.filter, this.isIntersectionFilter);

    for (let [model, ids] of idsByModel) {

      facetTracker.nextModel(model, ids);

      const classifiedAssets = this._classifiedAssets.get(model);
      if (!classifiedAssets) {
        for (let dbId of ids) {
          facetTracker.trackElementFacet(dbId, null);
        }
        continue;
      }

      for (let dbId of ids) {
        const row = classifiedAssets.rowByDbId[dbId];
        if (!row) {
          facetTracker.trackElementFacet(dbId, null);
          continue;
        }

        let isNull = true;
        facetTracker.nextElement(dbId);

        const propsWithStreamReading = classifiedAssets.dbIdToPropsWithStreamReading?.[dbId];

        for (let key in row) {

          const property = classifiedAssets.propertyById.get(key);
          if (!property) {
            continue;
          }

          isNull = false;

          const value = row[key];
          // Not tagged will be either null, undefined, or "".
          const isTagged = !(value == null || value === "") || propsWithStreamReading?.has(key);
          const id = `${property.uuid}|${isTagged ? 'tagged' : 'untagged'}`;

          facetTracker.addFacet({ id, property, isTagged }, id);
        }
        if (isNull) {
          facetTracker.addFacet(null);
        }
      }
    }
    return facetTracker.done(idsByModel);

  }

  async _cacheExternalDataForModel(model) {
    const classifiedAssets = await this._getClassifiedAssets(model);
    if (model.isDefault()) {
      classifiedAssets.dbIdToPropsWithStreamReading = await this._getDbIdToPropsWithStreamReading(model.getParentFacility());
    }
    this._classifiedAssets.set(model, classifiedAssets);
  }

  clearCachedExternalDataForModel(model) {
    this._classifiedAssets.delete(model);
  }

  getSorter() {
    return sortByLabel([UNASSIGNED]);
  }

  /**
   * Return a 2-level hierarchy:
   * - first level: the property name
   * - second level: the tagged status: "Tagged" or "Untagged"
   */
  formatMergedFacetNodes(mergedFacetNodes) {
    const listWithHierarchy = [];
    const paramNodesByUuid = {};

    for (const node of mergedFacetNodes) {
      const { id, isTagged, isType } = node;
      if (id === UNASSIGNED) {
        if (!node.children) node.children = [];
        listWithHierarchy.push(node);
        continue;
      }

      const [uuid] = id.split('|');
      let paramNode = paramNodesByUuid[uuid];
      if (!paramNode) {
        paramNode = new FacetNode({
          id: uuid,
          label: node.label,
          isType,
          children: [],
          selected: this.filter.has(id)
        });
        // Overriding total count as children is a single level partition, so total will always be x2.
        paramNode.getTotalCount = () => paramNode.count;
        paramNodesByUuid[uuid] = paramNode;
        listWithHierarchy.push(paramNode);
      }

      paramNode.children.push(node);
      paramNode.count += node.count;
      if (!node.children) node.children = [];
      node.label = isTagged ? i18n.t('Tagged') : i18n.t('Untagged');
    }
    return listWithHierarchy.sort(this.getSorter());
  }
}

export class FacetStatus extends FacetDef {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.status;
    this.label = i18n.t("Status");
    this._dbId2Status = new Map();
  }

  static get Type() {
    return {
      notStarted: { id: "Not started", label: i18n.t("Not started") },
      inProgress: { id: "In progress", label: i18n.t("In progress") },
      complete: { id: "Complete", label: i18n.t("Complete") },
      undefined: { id: UNDEFINED, label: undefinedLabel(), isUnassignedId: true }
    };
  }

  async _cacheExternalDataForModel(model) {
    if (!this._dbId2Status.has(model)) {
      this._dbId2Status.set(model, await this._fetchDbId2Status(model));
    }
  }
  clearCachedExternalDataForModel(model) {
    this._dbId2Status.delete(model);
  }


  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {
    const makeFacetItem = (status) => {
      return status || FacetStatus.Type.undefined;
    };
    const facetTracker = new FacetTracker(makeFacetItem, ignoreFilter ? new Set() : this.filter);

    for (let [model, ids] of idsByModel) {

      facetTracker.nextModel(model, ids);

      const dbId2Status = this._dbId2Status.get(model) || {};
      const it = model.getInstanceTree();
      for (let dbId of ids) {
        let status = dbId2Status[dbId];
        if (!status && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          status = dbId2Status[dbId];
        }
        facetTracker.trackElementFacet(dbId, status, status?.id);
      }
    }
    return facetTracker.done(idsByModel);
  }

  async _fetchDbId2Status(model) {
    const classifiedAssets = await model.getClassifiedAssets();
    const dbId2status = {};

    for (const row of classifiedAssets.rows) {

      let countCustomProps = 0;
      let countCustomPropsWithValue = 0;

      for (const key of Object.keys(row)) {
        const isPropKey = key.startsWith(`${DtConstants.ColumnFamilies.DtProperties}:`);
        if (isPropKey) {
          countCustomProps++;
          if (row[key] !== null) {
            countCustomPropsWithValue++;
          }
        }
      }

      if (countCustomProps > 0) {
        let status;
        if (countCustomPropsWithValue == countCustomProps) {
          status = FacetStatus.Type.complete;
        } else if (countCustomPropsWithValue > 0) {
          status = FacetStatus.Type.inProgress;
        } else {
          status = FacetStatus.Type.notStarted;
        }
        const dbId = row[DtConstants.QC.LmvDbId];
        dbId2status[dbId] = status;
      }
    }
    return dbId2status;
  }

  getSorter() {
    return (a, b) => {
      const order = [FacetStatus.Type.notStarted.id, FacetStatus.Type.inProgress.id, FacetStatus.Type.complete.id, UNDEFINED];
      return order.indexOf(a.id) - order.indexOf(b.id);
    };
  }
}

export class FacetClassifications extends FacetDef {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.classifications;
    this.label = i18n.t("Classifications");
    this.palette = Object.values(PALETTE_SYSTEMS);
    this.paletteById = PALETTE_SYSTEMS;
  }

  async init(settings, facility, options) {
    await super.init(settings, facility, options);
    this.facility = facility;
    const classification = await this._getClassification();
    this.setClassification(classification);
  }

  _getClassification() {
    return this.facility.getClassificationTemplate();
  }

  setClassification(classification) {
    this.classification = classification;

    // Cache classification tree
    this.classificationRoot = FacetClassifications.generateClassificationTree(this.classification);
  }

  static generateClassificationTree(classification) {
    // Cache classification tree
    const classificationRoot = { id: ROOT_ID, children: [] };

    // To reconstruct the tree, we run through rows and keep track of the last parents in a stack.
    // When going one level deeper, each next row (at the same level) are siblings and their parent is the previous row
    // Example of a classification table:
    // [code, description, level]
    // ["A", "Substructure", 1]
    // ["A10", "Foundations", 2]
    // ["A1010", "Standard Foundations", 3]
    // ["A1010.10", "Wall Foundations", 4]
    // ["A1010.30", "Column Foundations", 4]
    // ["A1010.90", "Standard Foundation Supplementary Components", 4]
    // ["A1020", "Special Foundations", 3]
    // ["A1020.10", "Driven Piles", 4]
    // ...
    // ["A1020.80", "Grade Beams", 4]
    // ["A20", "Subgrade Enclosures", 2]

    const nodeById = {};
    // running stack of parents
    const parentsIndices = [];

    const rows = classification?.rows ?? [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      nodeById[row[0]] = { id: row[0], label: row[1], children: [] };

      // compare to previous row
      // if level increases, push parent
      // if level decreases, pop parent
      if (i > 0) {
        const lastRow = rows[i - 1];
        if (row[2] > lastRow[2]) {
          parentsIndices.push(i - 1);
        } else if (row[2] < lastRow[2]) {
          for (let j = 0; j < lastRow[2] - row[2]; j++) {
            parentsIndices.pop();
          }
        }
      }
      // Current node parent is at the top of the stack
      const lastParentRow = rows[parentsIndices[parentsIndices.length - 1]];
      const currentNode = nodeById[row[0]];
      if (lastParentRow === undefined) {
        // Root nodes don't have a parent
        classificationRoot.children.push(currentNode);
      } else {
        const lastParentNode = nodeById[lastParentRow[0]];
        lastParentNode.children.push(currentNode);
      }
    }

    // Add unclassified
    classificationRoot.children.push({
      id: UNCLASSIFIED,
      label: unclassifiedLabel(),
      children: [],
      isUnassignedId: true
    });

    return classificationRoot;
  }

  getDbId2Class(model) {
    return (dbId) => model.getElementCustomClass(dbId);
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const filter = ignoreFilter ? new Set() : this.filter;
    if (!this.classification) {
      return makeNullBatch(UNCLASSIFIED, unclassifiedLabel(), idsByModel, filter);
    }


    const code2label = {};
    {
      const rows = this.classification.rows ?? [];
      for (let [code, label] of rows) {
        code2label[code] = label;
      }
    }

    const makeFacetItem = (code) => {
      return code ? {
        id: code,
        label: code2label[code],
        description: code
      } : {
        id: UNCLASSIFIED,
        label: unclassifiedLabel(),
        isUnassignedId: true
      };
    };

    const facetTracker = new FacetTracker(makeFacetItem, filter);

    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);

      const dbId2ClassFn = this.getDbId2Class(model);
      const it = model.getInstanceTree();

      for (let dbId of ids) {
        let elClass = dbId2ClassFn(dbId);
        if (!elClass && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          elClass = dbId2ClassFn(dbId);
        }
        facetTracker.trackElementFacet(dbId, elClass);
      }
    }

    return facetTracker.done(idsByModel);

  }

  /**
   * Rearrange facet nodes into a tree to reflect the hierarchical nature of classes
   * @param {FacetNode[]} mergedFacetNodes
   * @returns {FacetNode[]}
   */
  formatHierarchy(mergedFacetNodes) {
    // Reconstruct classification tree by keeping only given mergedFacetNodes and eventually adding missing parents
    const dfs = (node, mergedFacetNodeById) => {
      if (!node) {
        return { found: false, node: null };
      }

      let found = node.id in mergedFacetNodeById;

      const children = (node.children || []).
      map((child) => {
        const next = dfs(child, mergedFacetNodeById);
        found = found || next.found;
        return next.node;
      }).
      filter((n) => n !== null);

      if (found) {
        let mergedFacetNode = mergedFacetNodeById[node.id];
        if (!mergedFacetNode) {
          // Add missing parent
          mergedFacetNode = new FacetNode({
            id: node.id,
            label: node.label,
            description: node.id,
            selected: this.filter.has(node.id),
            implicitlySelected: this.filter.size === 0
          });
          mergedFacetNodeById[node.id] = mergedFacetNode;
        }
        mergedFacetNode.children = children;
        return { found, node: mergedFacetNode };
      }

      return { found, node: null };
    };

    // Accumulate given mergedFacetNodes by id
    const mergedFacetNodeById = { [ROOT_ID]: {} };
    for (const mergedFacetNode of mergedFacetNodes) {
      mergedFacetNodeById[mergedFacetNode.id] = mergedFacetNode;
    }

    const { node: root } = dfs(this.classificationRoot, mergedFacetNodeById);
    return root?.children ?? [];
  }

  getSorter() {
    return (a, b) => {
      // last
      if (a.id === UNCLASSIFIED) {
        if (b.id === UNCLASSIFIED)
        return 0;else

        return 1;
      } else if (b.id === UNCLASSIFIED) {
        return -1;
      }

      return a.label.localeCompare(b.label);
    };
  }

  formatMergedFacetNodes(mergedFacetNodes) {
    return this.formatHierarchy(mergedFacetNodes);
  }

  addFilter(value) {
    if (value.endsWith("/*")) {
      const prefix = value.slice(0, -2);
      if (this.classification) {
        for (let [code] of this.classification.rows) {
          if (matchClassification(code, prefix)) {
            this.filter.add(code);
          }
        }
      } else {
        console.warn("classification system missing");
      }
      this.filter.add(prefix);
    } else {
      this.filter.add(value);
    }
  }

  supportsWildcardFilters() {
    return true;
  }
}

export class FacetAssemblyCode extends FacetClassifications {
  constructor() {
    super();
    this.id = FacetTypes.assemblyCode;
    this.label = i18n.t("Assembly Code");
  }

  getDbId2Class(model) {
    return (dbId) => model.getElementUfClass(dbId);
  }

  _getClassification() {
    return DtConstants.getUniformatClassificationSystem();
  }
}

export class FacetTandemCategories extends FacetClassifications {
  constructor() {
    super();
    this.id = FacetTypes.tandemCategories;
    this.label = i18n.t("Tandem Category");
    this.palette = Object.values(PALETTE_TANDEM_CATS);
    this.paletteById = PALETTE_TANDEM_CATS;

  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {



    const unclassified = {
      id: UNCLASSIFIED,
      label: unclassifiedLabel(),
      isUnassignedId: true
    };

    const rows = [...TandemCategories.classificationSystem.rows];
    const code2row = {};
    for (let i = 0; i < rows.length; i++) {
      const [code, label] = rows[i];
      code2row[code] = {
        id: code,
        label: label,
        description: code
      };
    }
    rows.unshift([]);

    const makeFacetItem = (code) => {
      return code2row[code] || unclassified;
    };



    const facetTracker = new FacetTracker(makeFacetItem, ignoreFilter ? new Set() : this.filter);

    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);

      const dbId2TandemCategory = model.getData().dbId2TandemCategory;
      const it = model.getInstanceTree();
      for (let dbId of ids) {
        let tcId = dbId2TandemCategory[dbId];
        if (!tcId && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          tcId = dbId2TandemCategory[dbId];
        }
        let [code] = rows[tcId];
        facetTracker.trackElementFacet(dbId, code);
      }
    }

    return facetTracker.done(idsByModel);

  }

  _getClassification() {
    return TandemCategories.classificationSystem;
  }
}

export class FacetAttribute extends FacetDef {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.attributes;
    this.label = ""; // attribute name
    this.palette = PALETTE_SOURCE_FILES;
    this.attribute = null;
    this.facility = null;
    this._customFacetData = new Map();
  }

  async init(settings, facility, options) {
    super.init(settings, facility, options);
    this.facility = facility;
  }

  setSettings(settings) {
    super.setSettings(settings);
    this.settings.attributeHash = settings.attributeHash;
    this.settings.buckets = settings.buckets;
    this.settings.bucketDef = settings.bucketDef;
    // we allow multiple facets of type "attributes". They differ from the attribute used
    this.id = FacetTypes.attributes + "_" + this.settings.attributeHash;
  }

  setAttribute(attribute) {
    this.attribute = attribute;
    this.label = attribute.displayName;

    if (this.attribute.dataType === AttributeType.StringList) {
      this.palette = PALETTE_ATTRIBUTE_STRINGLIST;
    } else if (this.attribute.allowedValues?.list?.length > 0) {
      this.palette = PALETTE_ATTRIBUTE_STRING;
    }
  }

  isLoaded() {
    return this.attribute !== null;
  }

  isSystemClassificationAttribute() {
    return this.attribute.category === "Mechanical" && this.attribute.name === "System Classification";
  }

  async _cacheExternalDataForModel(model) {
    if (!this._customFacetData.has(model)) {
      // TODO: the existing implementation is more optimal, as it uses a shared data shard for all custom attributes
      const { attributeHash, buckets, bucketDef } = this.settings;
      const facets = await model.getCustomFacetsDebounced([attributeHash], [buckets], [bucketDef], [true]);
      this._customFacetData.set(model, facets?.[0]);
    }
  }
  clearCachedExternalDataForModel(model) {
    this._customFacetData.delete(model);
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const filter = ignoreFilter ? new Set() : this.filter;
    if (!this.attribute) {
      return makeNullBatch(UNDEFINED, undefinedLabel(), idsByModel, filter);
    }

    let makeFacetItem;
    {
      const nullItem = {
        id: UNDEFINED,
        label: undefinedLabel(),
        isUnassignedId: true
      };

      if (this.isSystemClassificationAttribute() || this.attribute.dataType === AttributeType.StringList) {
        makeFacetItem = (value) => {
          if (!value || value === UNDEFINED) return nullItem;

          return {
            id: value,
            label: value
          };
        };
      } else if (this.attribute.dataType === AttributeType.Url) {
        makeFacetItem = (value) => {
          if (!value) return nullItem;

          const document = this.facility.settings.docs?.find((doc) => doc.id === value);
          if (document) {
            return {
              id: value,
              label: document.name
            };
          }

          try {
            const url = new URL(value);
            if (url.protocol === 'http:' || url.protocol === 'https:') {
              return {
                id: value,
                label: value
              };
            }
          } catch (e) {}

          return nullItem;
        };
      } else {
        makeFacetItem = (bucket) => bucket || nullItem;
      }
    }

    const facetTracker = new FacetTracker(makeFacetItem, filter, this.isIntersectionFilter);


    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);

      const customFacet = this._customFacetData.get(model);
      if (!customFacet) {
        for (let dbId of ids) {
          facetTracker.trackElementFacet(dbId, null);
        }
        continue;
      }

      const { dbId2bucketIdx, buckets } = customFacet;
      const it = model.getInstanceTree();

      for (let dbId of ids) {
        let bucketIdx = dbId2bucketIdx[dbId];
        if (!bucketIdx && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          bucketIdx = dbId2bucketIdx[dbId];
        }
        if (!bucketIdx) {
          facetTracker.trackElementFacet(dbId, null);
          continue;
        }

        let bucket = buckets[bucketIdx];

        if (this.isSystemClassificationAttribute()) {
          facetTracker.nextElement(dbId);

          //Special classifier for Revit System Classification property.
          //In Revit, that's encoded as a comma separated list of Revit Systems,
          //so we split the string value and generate multiple possible facets for each element
          //
          //Also note that it's not uncommon for this field to contain duplicates, which we
          //skip here to avoid overcounting.
          let values = bucket.value.split(",").sort();
          let previous;
          for (let v of values) {
            if (v !== previous) {
              facetTracker.addFacet(v);
            }
            previous = v;
          }

        } else if (this.attribute.dataType === AttributeType.StringList) {
          facetTracker.nextElement(dbId);

          // Special classifier for Tags attributes
          // Attributes of type StringList are stored as a list of strings,
          // so we generate a facet for each value in the list
          for (let v of bucket.value) {
            facetTracker.addFacet(v);
          }
        } else if (this.attribute.dataType === AttributeType.Url) {
          facetTracker.nextElement(dbId);
          const docIds = bucket.value.split('|');
          for (const docId of docIds) {
            facetTracker.addFacet(docId);
          }
        } else {
          facetTracker.trackElementFacet(dbId, bucket, bucket.id);
        }
      }
    }
    return facetTracker.done(idsByModel);
  }

  getSorter() {
    return (a, b) => {
      // last
      if (a.id === UNDEFINED || a.label === undefined) {
        if (b.id === UNDEFINED || b.label === undefined)
        return 0;else

        return 1;
      } else if (b.id === UNDEFINED || b.label === undefined) {
        return -1;
      }

      // retain order of user provided buckets
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }

      let dt = this.attribute?.dataType || AttributeType.Unknown;
      if (dt === AttributeType.Integer ||
      dt === AttributeType.Double ||
      dt === AttributeType.Float)
      {
        return a.value - b.value;
      } else if (dt === AttributeType.DateTime && a.value && b.value) {
        return a.value.getTime() - b.value.getTime();
      }

      return a.label.toString().localeCompare(b.label.toString());
    };
  }

  /** Group dates by year, leave everything else untouched */
  formatMergedFacetNodes(mergedFacetNodes) {
    if (this.attribute?.dataType !== AttributeType.DateTime) {
      return mergedFacetNodes.sort(this.getSorter());
    }

    const listWithHierarchy = [];
    const years = {};

    for (const node of mergedFacetNodes) {
      const { id, isType } = node;
      if (id === UNDEFINED) {
        if (!node.children) node.children = [];
        listWithHierarchy.push(node);
        continue;
      }

      let year, label;
      if (this.settings.bucketDef) {
        [, year] = id.split('/');
        label = node.label;
      } else {
        const date = new Date(node.value);
        year = date.getFullYear().toString();
        label = node.value;
      }

      if (!label || !year) {
        if (!node.children) node.children = [];
        listWithHierarchy.push(node);
        continue;
      }

      let yearNode = years[year];
      if (!yearNode) {
        yearNode = new FacetNode({
          id: year,
          label: year,
          isType,
          children: [],
          selected: this.filter.has(id),
          implicitlySelected: this.filter.size === 0
        });
        yearNode.getTotalCount = () => yearNode.count;
        years[year] = yearNode;
        listWithHierarchy.push(yearNode);
      }

      yearNode.children.push(node);
      yearNode.count += node.count;
      if (!node.children) node.children = [];
      node.label = label;
    }

    listWithHierarchy.forEach((node) => node.children?.sort(this.getSorter()));
    return listWithHierarchy.sort(this.getSorter());
  }

  formatValue(value) {
    const forgeUnit = this.attribute.forgeUnit ? "autodesk.unit.unit:" + this.attribute.forgeUnit + "-1.0.0" : this.attribute.dataTypeContext;
    return formatValueWithUnits(value, forgeUnit, this.attribute.dataType, this.attribute.precision, { symbolKey: this.attribute.forgeSymbol });
  }

  supportsBucketing() {
    switch (this.attribute?.dataType) {
      case AttributeType.Integer:
      case AttributeType.Float:
      case AttributeType.Double:
      case AttributeType.DateTime:
        return true;
    }
    return false;
  }

  deriveBuckets(facets, options) {

    const mergedOptions = {
      numBuckets: 5,
      mode: 'count', // count | distance
      ...options
    };

    // assumes 'facets' is produced by this instance (hence sorted etc)
    const noUnassigned = facets.filter((f) => !f.isUnassignedId);
    const min = noUnassigned.at(0).value;
    const max = noUnassigned.at(-1).value;

    if (min === undefined || max === undefined) return; // already bucketed or empty facets

    const buckets = [];
    let currentBucket = {};
    switch (mergedOptions.mode) {
      case 'count':
        if (noUnassigned.length < mergedOptions.numBuckets) return;

        // tries to make buckets with roughly even number of assets inside
        const count = noUnassigned.reduce((sum, f) => sum += f.count, 0);
        let bucketSize = count / mergedOptions.numBuckets;

        let itemsInBucket = 0;
        for (let facet of noUnassigned) {
          itemsInBucket += facet.count;
          if (!currentBucket.lower) {
            currentBucket.lower = facet.value;
          }
          currentBucket.upper = facet.value;

          if (itemsInBucket > bucketSize) {
            buckets.push(currentBucket);
            currentBucket = {};
            itemsInBucket = 0;
          }
        }
        if (itemsInBucket) {
          buckets.push(currentBucket);
        }
        break;
      case 'distance':
        if (noUnassigned.length < mergedOptions.numBuckets) return;

        // distance based distribution. aims for buckets of roughly equal size (upper-lower).
        const d = (max - min) / mergedOptions.numBuckets;

        let upper = min + d;
        for (let facet of noUnassigned) {
          if (facet.value > upper) {
            buckets.push(currentBucket);
            currentBucket = {};
            upper += d;
          }
          if (!currentBucket.lower) {
            currentBucket.lower = facet.value;
          }
          currentBucket.upper = facet.value;
        }
        buckets.push(currentBucket);
        break;
      default:
        console.error("Unknown bucketing: " + mode);
        return;
    }

    // first and last bucket are half open
    delete buckets[0].lower;
    delete buckets.at(-1).upper;

    // shuffle the boundaries around so they look nicer
    for (let i = 1; i < buckets.length; ++i) {
      const u = buckets[i - 1].upper;
      const l = buckets[i].lower;
      let m = (u + l) / 2;

      for (let n of MULTIPLES) {
        m = roundToNearestMultiple(m, n);
        if (u <= m && m < l) {
          buckets[i - 1].upper = buckets[i].lower = m;
        } else {
          break;
        }
      }
    }

    // add counts for information
    for (let facet of noUnassigned) {
      const idx = findBucketIdxByValue(buckets, facet.value);
      if (idx >= 0) {
        buckets[idx].count = (buckets[idx].count || 0) + facet.count;
      }
    }

    // derive id and label
    for (let bucket of buckets) {
      if (bucket.lower === undefined) {
        bucket.id = `<${bucket.upper}`;
        bucket.label = `< ${this.formatValue(bucket.upper)}`;
      } else if (bucket.upper === undefined) {
        bucket.id = `>${bucket.lower}`;
        bucket.label = `> ${this.formatValue(bucket.lower)}`;
      } else {
        bucket.id = `${bucket.lower}-${bucket.upper}`;
        bucket.label = `${Number.parseFloat(this.formatValue(bucket.lower))} - ${this.formatValue(bucket.upper)}`;
      }
    }

    return buckets;
  }
}

const MULTIPLES = [0.01, 0.1, 1, 2, 5, 10, 100, 1000, 10000, 100000];
function roundToNearestMultiple(v, n) {
  return Math.round(v / n) * n;
}

export class FacetModels extends FacetDef {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.models;
    this.label = i18n.t("Sources");
    this.palette = PALETTE_SOURCE_FILES;
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const idsByModelFiltered = new Map();
    const facets = [];
    for (let [model, ids] of idsByModel) {
      // skip model nodes when not using them, except default to prevent it from being eagerly unloaded.
      if (ids.length === 0 && !model.isDefault()) continue;

      const selected = this.filter.has(model.urn());
      const implicitlySelected = this.filter.size == 0 && model.isVisibleByDefault();
      facets.push(new FacetNode({
        id: model.urn(),
        label: model.displayName(),
        selected,
        implicitlySelected,
        count: ids.length,
        ids: new Map([[model, ids]])
      }));

      if (selected || implicitlySelected) {
        idsByModelFiltered.set(model, ids);
      }
    }

    // returns set of facet nodes and a filtered-down version idsByModel to be passed into the next facet
    return {
      facets,
      idsByModelFiltered
    };
  }
}


export class FacetAssets extends FacetDef {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.assets;
    this.label = i18n.t("Assets");

    this.assetCheckForModel = new Map();
  }


  async _cacheExternalDataForModel(model) {
    if (!this.assetCheckForModel.has(model)) {
      const codesWithParams = model.facility.settings.template?.getCodesWithParameters() ?? {};
      this.assetCheckForModel.set(model, new AssetCheck(model, codesWithParams));
    }
  }

  clearCachedExternalDataForModel(model) {
    this.assetCheckForModel.delete(model);
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {
    const FACETID_ASSET = "ASSET";
    const FACEDID_NONASSET = "NONASSET";

    const filter = ignoreFilter ? new Set() : this.filter;

    const assets = new FacetNode({
      id: FACETID_ASSET,
      label: i18n.t("Assets"),
      selected: filter.has(FACETID_ASSET),
      implicitlySelected: filter.size === 0,
      count: 0,
      ids: new Map()
    });
    const nonAssets = new FacetNode({
      id: FACEDID_NONASSET,
      label: i18n.t("Other elements"),
      selected: filter.has(FACEDID_NONASSET),
      implicitlySelected: filter.size === 0,
      count: 0,
      ids: new Map()
    });

    for (let [model, ids] of idsByModel) {
      const chkr = this.assetCheckForModel.get(model);
      for (let dbId of ids) {
        let destFacet = chkr.isAsset(dbId) ? assets : nonAssets;
        destFacet.count++;
        if (destFacet.ids.has(model)) {
          destFacet.ids.get(model).push(dbId);
        } else {
          destFacet.ids.set(model, [dbId]);
        }
      }
    }

    let idsByModelFiltered;
    if (assets.selected == nonAssets.selected) {
      idsByModelFiltered = idsByModel;
    } else if (assets.selected) {
      idsByModelFiltered = assets.ids;
    } else if (nonAssets.selected) {
      idsByModelFiltered = nonAssets.ids;
    }

    return {
      facets: [assets, nonAssets],
      idsByModelFiltered
    };
  }
}
export class FacetRvtCategories extends FacetDef {

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.categories;
    this.label = i18n.t("Revit Categories");
  }

  async init(settings, facility, options) {
    await super.init(settings, facility, options);
    this.CATEGORIES = await DtConstants.getRevitCategories();
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const makeFacetItem = (id) => {
      return id ? {
        id,
        label: this.CATEGORIES[id]
      } : {
        id: UNASSIGNED,
        label: unclassifiedLabel(),
        isUnassignedId: true
      };
    };
    const facetTracker = new FacetTracker(makeFacetItem, ignoreFilter ? new Set() : this.filter);

    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);

      const dbId2catId = model.getData().dbId2catId;
      const it = model.getInstanceTree();
      for (let dbId of ids) {
        let cat = dbId2catId[dbId];
        if (!cat && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          cat = dbId2catId[dbId];
        }
        facetTracker.trackElementFacet(dbId, cat);
      }
    }
    return facetTracker.done(idsByModel);
  }
}

export class FacetRvtFamilies extends FacetDef {

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.families;
    this.label = i18n.t("Revit Families");
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const makeFacetItem = (familyName) => {
      return familyName ? {
        id: familyName
      } : {
        id: UNASSIGNED,
        label: unassignedLabel(),
        isUnassignedId: true
      };
    };
    const facetTracker = new FacetTracker(makeFacetItem, ignoreFilter ? new Set() : this.filter);

    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);

      let it = model.getInstanceTree();
      let dbId2ftypeId = model.getData().dbId2ftypeId;

      for (let dbId of ids) {
        let parentId = it.getNodeParentId(dbId2ftypeId[dbId]);
        // TODO: useLastObject???
        if (parentId) {
          facetTracker.trackElementFacet(dbId, it.getNodeName(parentId));
        } else {
          facetTracker.trackElementFacet(dbId, null);
        }
      }
    }
    return facetTracker.done(idsByModel);
  }
}

export class FacetRvtTypes extends FacetDef {

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.types;
    this.label = i18n.t("Revit Types");
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {

    const makeFacetItem = (familyName) => {
      return familyName ? {
        id: familyName
      } : {
        id: UNASSIGNED,
        label: unassignedLabel(),
        isUnassignedId: true
      };
    };
    const facetTracker = new FacetTracker(makeFacetItem, ignoreFilter ? new Set() : this.filter);

    for (let [model, ids] of idsByModel) {
      facetTracker.nextModel(model, ids);

      let it = model.getInstanceTree();
      let dbId2ftypeId = model.getData().dbId2ftypeId;

      for (let dbId of ids) {
        let ftypeId = dbId2ftypeId[dbId];
        //TODO useLastObject???
        if (ftypeId) {
          facetTracker.trackElementFacet(dbId, it.getNodeName(ftypeId));
        } else {
          facetTracker.trackElementFacet(dbId, null);
        }
      }
    }
    return facetTracker.done(idsByModel);
  }
}

/** Facet describing the state of tickets' QC.Priority. */
export class FacetPriority extends FacetDef {
  #priorityFacetByModel = new Map();

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.priority;
    this.label = i18n.t('Priority');

    this.palette = Object.values(PALETTE_TICKET_PRIORITY);
    this.paletteById = PALETTE_TICKET_PRIORITY;

    this.attr = DtConstants.StandardAttributes[DtConstants.QC.Priority];
  }

  async _cacheExternalDataForModel(model) {
    if (this.#priorityFacetByModel.has(model) || !model.isDefault()) return;

    const facets = await model.getCustomFacetsDebounced([this.attr.hash]);
    this.#priorityFacetByModel.set(model, facets?.[0]);
  }

  clearCachedExternalDataForModel(model) {
    this.#priorityFacetByModel.delete(model);
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {
    const nullItem = { id: UNDEFINED, label: undefinedLabel(), isUnassignedId: true };
    const makeFacetItem = (bucketId) => {
      return bucketId ? { id: bucketId, label: i18n.t(bucketId) } : nullItem;
    };
    const filter = ignoreFilter ? new Set() : this.filter;
    const tracker = new FacetTracker(makeFacetItem, filter, this.isIntersectionFilter);

    for (let [model, ids] of idsByModel) {
      tracker.nextModel(model, ids);

      const priorityFacet = this.#priorityFacetByModel.get(model);
      if (!priorityFacet) {
        for (let dbId of ids) {
          tracker.trackElementFacet(dbId, null);
        }
        continue;
      }

      const { dbId2bucketIdx, buckets } = priorityFacet;
      const it = model.getInstanceTree();

      for (let dbId of ids) {
        let bucketIdx = dbId2bucketIdx[dbId];
        if (!bucketIdx && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          bucketIdx = dbId2bucketIdx[dbId];
        }
        if (!bucketIdx) {
          tracker.trackElementFacet(dbId, null);
          continue;
        }

        tracker.trackElementFacet(dbId, buckets[bucketIdx]);
      }
    }
    return tracker.done(idsByModel);
  }
}

/** 
 * Facet describing the state of tickets' Open/Close status. 
 * Wrapper around ticket closeDate
 */
export class FacetTicketStatus extends FacetDef {
  #closeDateFacetByModel = new Map();

  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.ticketStatus;
    this.label = i18n.t('Status');
    this.filter = new Set([UNDEFINED]); // default to "Open" tickets

    this.attr = DtConstants.StandardAttributes[DtConstants.QC.CloseDate];
  }

  async _cacheExternalDataForModel(model) {
    if (this.#closeDateFacetByModel.has(model) || !model.isDefault()) return;

    const facets = await model.getCustomFacetsDebounced([this.attr.hash]);
    this.#closeDateFacetByModel.set(model, facets?.[0]);
  }

  clearCachedExternalDataForModel(model) {
    this.#closeDateFacetByModel.delete(model);
  }

  batchClassify(idsByModel, { useLastObject, ignoreFilter }) {
    const nullItem = { id: UNDEFINED, label: i18n.t('Open'), isUnassignedId: true };
    const makeFacetItem = (bucketId) => {
      return bucketId ? { id: bucketId, label: i18n.t(bucketId) } : nullItem;
    };
    const filter = ignoreFilter ? new Set() : this.filter;
    const tracker = new FacetTracker(makeFacetItem, filter, this.isIntersectionFilter);
    for (let [model, ids] of idsByModel) {
      tracker.nextModel(model, ids);

      const closeDateFacet = this.#closeDateFacetByModel.get(model);
      if (!closeDateFacet) {
        for (let dbId of ids) {
          tracker.trackElementFacet(dbId, null);
        }
        continue;
      }

      const { dbId2bucketIdx } = closeDateFacet;
      const it = model.getInstanceTree();

      for (let dbId of ids) {
        let bucketIdx = dbId2bucketIdx[dbId];
        if (!bucketIdx && useLastObject) {
          dbId = it.findNodeForSelection(dbId, SelectionMode.LAST_OBJECT);
          bucketIdx = dbId2bucketIdx[dbId];
        }
        if (!bucketIdx) {
          tracker.trackElementFacet(dbId, null);
          continue;
        }

        tracker.trackElementFacet(dbId, 'Closed');
      }
    }
    return tracker.done(idsByModel);
  }

  // Sort "Open" before "Closed"
  getSorter() {
    return (a, b) => {
      if (a.id === UNDEFINED) return -1;
      if (b.id === UNDEFINED) return 1;
      return 0;
    };
  }

  resetFilters() {
    super.resetFilters();
    this.filter.add(UNDEFINED); // default to "Open" tickets
  }
}

export class FacetOpenDate extends FacetAttribute {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.openDate;
    this.label = i18n.t('Open Date');
    this.attribute = DtConstants.StandardAttributes[DtConstants.QC.OpenDate];
    this.settings = {
      attributeHash: this.attribute.hash
    };
  }

  setSettings(settings) {
    this.settings.buckets = settings.buckets;
    this.settings.bucketDef = settings.bucketDef;
  }

  async _cacheExternalDataForModel(model) {
    if (model.isDefault()) {
      await super._cacheExternalDataForModel(model);
    };
  }
}

export class FacetCloseDate extends FacetAttribute {
  constructor(hidden) {
    super(hidden);
    this.id = FacetTypes.closeDate;
    this.label = i18n.t('Close Date');
    this.attribute = DtConstants.StandardAttributes[DtConstants.QC.CloseDate];
    this.settings = {
      attributeHash: this.attribute.hash
    };
  }

  setSettings(settings) {
    this.settings.buckets = settings.buckets;
    this.settings.bucketDef = settings.bucketDef;
  }

  async _cacheExternalDataForModel(model) {
    if (model.isDefault()) {
      await super._cacheExternalDataForModel(model);
    };
  }
}

//TODO: stop exporting this raw object, it makes it impossible to hide a facet type behind a feature flag
export const FacetRegistry = {
  [FacetTypes.levels]: FacetLevels,
  [FacetTypes.tandemCategories]: FacetTandemCategories,
  [FacetTypes.assemblyCode]: FacetAssemblyCode,
  [FacetTypes.classifications]: FacetClassifications,
  [FacetTypes.attributes]: FacetAttribute,
  [FacetTypes.models]: FacetModels,
  [FacetTypes.categories]: FacetRvtCategories,
  [FacetTypes.families]: FacetRvtFamilies,
  [FacetTypes.types]: FacetRvtTypes,
  [FacetTypes.spaces]: FacetSpaces,
  [FacetTypes.status]: FacetStatus,
  [FacetTypes.systemClasses]: FacetSystemClasses,
  [FacetTypes.mepSystems]: FacetSystems,
  [FacetTypes.parameters]: FacetParameters,
  [FacetTypes.priority]: FacetPriority,
  [FacetTypes.ticketStatus]: FacetTicketStatus,
  [FacetTypes.openDate]: FacetOpenDate,
  [FacetTypes.closeDate]: FacetCloseDate
  //[FacetTypes.assets]: FacetAssets,  
};

export function deserializeFacet(facetDefId) {
  let ctor;

  for (let type in FacetRegistry) {
    // Facet ids are prefixed by their types
    if (facetDefId.startsWith(type)) {
      ctor = FacetRegistry[type];
      break;
    }
  }

  if (!ctor) {
    console.error("Cannot instantiate facet for", facetDefId);
    return null;
  }

  return new ctor();
}

export function getDefaultFacetList(facility, wantClassification) {
  if (wantClassification == null) {
    // matches the desired default when loading a facility
    const clsId = facility.settings?.template?.getClassificationID();
    wantClassification = Boolean(clsId) && !DtConstants.isBuiltInClassificationSystem(clsId);
  }

  const schemaVersion = facility.getSchemaVersion();
  const defaultFacetList = [
  new FacetModels(),
  new FacetLevels(),
  new FacetSpaces(),
  wantClassification && new FacetClassifications(),
  schemaVersion === 1 ? new FacetAssemblyCode() : new FacetTandemCategories(),
  new FacetRvtCategories()
  //FacetRvtFamilies, //off by default
  //FacetRvtTypes, //off by default
  //FacetSystems, //off by default
  ];
  return defaultFacetList.filter(Boolean);
}

export function getFacetListForScope(scope, facility, wantClassificationFacet) {
  switch (scope) {
    case FacetsScope.Tickets:
      return [
      new FacetModels(true),
      new FacetLevels(true),
      new FacetSpaces(true),
      new FacetPriority(),
      new FacetTicketStatus()];

    case FacetsScope.Context:
    default:
      return getDefaultFacetList(facility, wantClassificationFacet);
  }
}

class FacetNode {
  count = 0;
  ids = new Map();
  children = null;

  constructor(props) {
    Object.assign(this, props);
  }

  add(model, dbId) {
    if (this.ids.has(model)) {
      this.ids.get(model).push(dbId);
    } else {
      this.ids.set(model, [dbId]);
    }
    this.count++;
  }

  getTotalCount() {
    let total = this.count;
    if (this.children) {
      for (let child of this.children) {
        total += child.getTotalCount();
      }
    }
    return total;
  }
}

class FacetTracker {

  currentModel = null;
  passedElements = []; // list of dbIds (from currentModel)
  idsInMultipleFacets = []; // list of dbIds (from currentModel)

  currentElement = -1; // dbId
  facetsOfCurrentElement = []; // list of FacetNodes

  facetItemCache = new Map();
  idsByModelFiltered = new Map();
  idsInMultipleFacetsByModel = new Map();

  constructor(makeFacetItemCallback, filter, isIntersectionFilter) {
    this.makeFacetItemCallback = makeFacetItemCallback;
    this.filter = filter;
    this.hasFilter = filter.size > 0;
    this.isIntersectionFilter = isIntersectionFilter;
  }

  #getFacet(value, cacheKey) {
    let cached = this.facetItemCache.get(cacheKey || value);
    if (!cached) {
      cached = new FacetNode(this.makeFacetItemCallback(value));
      cached.selected = this.filter.has(cached.id);
      cached.implicitlySelected = !this.hasFilter;
      this.facetItemCache.set(cacheKey || value, cached);
    }

    return cached;
  }

  // Adds an empty facet if none existed with this ID/cache key
  addIfMissing(value, cacheKey) {
    let cached = this.#getFacet(value, cacheKey);
    if (!cached.count) {
      cached.allowedEmpty = true;
    }
  }

  nextModel(model, ids) {

    if (this.currentModel) {
      // conclude the current element, if any
      this.nextElement(null);

      if (this.passedElements.length) {
        this.idsByModelFiltered.set(this.currentModel, this.passedElements);
      }

      if (this.idsInMultipleFacets.length) {
        this.idsInMultipleFacetsByModel.set(this.currentModel, this.idsInMultipleFacets);
      }
    }
    this.currentModel = model;
    this.passedElements = this.hasFilter ? [] : ids;
    this.idsInMultipleFacets = [];
  }

  // called by if one element only ever maps to exactly one facet (or the undefined)
  trackElementFacet(dbId, value, cacheKey) {
    const facet = this.#getFacet(value, cacheKey);
    facet.add(this.currentModel, dbId);
    if (this.hasFilter && facet.selected) {
      this.passedElements.push(dbId);
    }
  }

  // the two functions below are called if an element (potentially) belongs to multiple facets
  nextElement(dbId) {
    // finish previous element
    if (this.facetsOfCurrentElement.length) {
      if (this.hasFilter && !this.isIntersectionFilter && this.facetsOfCurrentElement.some((f) => f.selected))
      {
        this.passedElements.push(this.currentElement);
      }
      if (this.facetsOfCurrentElement.length > 1) {
        this.idsInMultipleFacets.push(this.currentElement);
      }
      this.facetsOfCurrentElement.length = 0;
    }
    this.currentElement = dbId;
  }

  addFacet(value, cacheKey) {
    const facet = this.#getFacet(value, cacheKey);
    if (this.facetsOfCurrentElement.includes(facet)) {
      console.warn("(Unexpected) Skipping duplicate facet item", facet.id, this.currentElement, this.currentModel.urn());
      return;
    }
    facet.add(this.currentModel, this.currentElement);
    this.facetsOfCurrentElement.push(facet);
  }

  // called after looping over all models
  done(idsByModel, allowEmptyFacets) {
    // conclude the current model, if any
    this.nextModel(null);

    const facets = Array.from(this.facetItemCache.values());

    // special handling of intersection filters
    if (this.hasFilter && this.isIntersectionFilter) {
      const selectedFacets = facets.filter((f) => f.selected);
      if (selectedFacets.length == 1) {
        this.idsByModelFiltered = selectedFacets[0].ids;
      } else if (selectedFacets.length > 1) {
        for (let [model, ids] of this.idsInMultipleFacetsByModel) {
          for (let facet of selectedFacets) {
            let facetIds = facet.ids.get(model);
            if (facetIds) {
              ids = sortedArrayIntersection(ids, facetIds);
            }
            if (!ids.length) {
              break;
            }
          }

          if (ids.length) {
            this.idsByModelFiltered.set(model, ids);
          }
        }
      }
    }

    let idsByModelFiltered = this.idsByModelFiltered;
    if (!allowEmptyFacets && this.idsByModelFiltered.size === 0) {
      // If nothing passed the filter, we let everything pass.
      // Note that this.filter might be non empty, yet none of its entries
      // apply due to a higher level facet's filter.
      idsByModelFiltered = idsByModel;
    }

    return {
      facets,
      idsByModelFiltered,
      idsInMultipleFacetsByModel: this.idsInMultipleFacetsByModel
    };
  }
}

function getTotalIdCount(idsByModel) {
  let sum = 0;
  for (let ids of idsByModel.values()) {
    sum += ids.size || ids.length;
  }
  return sum;
}